import { badgeOverlayDataUrl } from '@/features/desktop/badgeImage';
import { desktopBridge } from '@/features/desktop/bridge';

const DEFAULT_TITLE = 'Cyphex Teams';
const MAX_TITLE_COUNT = 99;

/**
 * THE TAB TITLE HAS TWO AUTHORS, AND THEY WERE FIGHTING.
 *
 * Admin → Org → Branding promises that the product name "replaces the product
 * name in the header, page titles and emails", and `useApplyBranding` duly set
 * `document.title = appName`. Then the very next unread-count tick came through
 * here and wrote `(8) Cyphex Teams` over it from a hard-coded constant. On any
 * workspace with unread messages — which is every real one — a branded operator
 * never saw their own name in the tab at all. Reproduced on the live site: the
 * shell rendered "Northwind Field Ops" in the header while the tab still said
 * "(8) Cyphex Teams".
 *
 * So the count and the name are kept as two pieces of state in ONE writer.
 * Whichever changes, the title is repainted from both.
 */
let baseTitle = DEFAULT_TITLE;
let unread = 0;

function paintTitle(): void {
  if (typeof document === 'undefined') return;
  const label = unread > MAX_TITLE_COUNT ? `${MAX_TITLE_COUNT}+` : String(unread);
  document.title = unread > 0 ? `(${label}) ${baseTitle}` : baseTitle;
}

/**
 * The workspace's product name, from `useApplyBranding`. Empty falls back to the
 * shipped name rather than leaving a blank tab.
 */
export function setBaseTitle(name: string): void {
  baseTitle = name.trim() === '' ? DEFAULT_TITLE : name.trim();
  paintTitle();
}

/**
 * Reflects the unread count on the OS/taskbar icon and the tab title.
 * The Badging API is not universal, so both calls are feature-detected and
 * failures are swallowed — a missing badge must never break the app.
 *
 * In the desktop shell the count goes to the main process instead. The Badging
 * API cannot serve it there: Windows has no numeric taskbar badge at all (the
 * platform primitive is an overlay IMAGE), and only the main process can touch
 * the dock count or the tray icon.
 */
export function applyBadgeCount(count: number): void {
  const bridge = desktopBridge();
  if (bridge !== null) {
    void bridge.setBadge(count, badgeOverlayDataUrl(count)).catch(() => undefined);
  } else if (typeof navigator !== 'undefined') {
    try {
      if (count > 0 && 'setAppBadge' in navigator) {
        void navigator.setAppBadge(count).catch(() => undefined);
      } else if (count <= 0 && 'clearAppBadge' in navigator) {
        void navigator.clearAppBadge().catch(() => undefined);
      }
    } catch {
      /* Badging is unsupported or blocked in this context. */
    }
  }

  unread = count;
  paintTitle();
}
